<script lang="ts" setup>
import {
  defineOgImageComponent,
  useHead,
  useRoute,
  useSpots,
  computed,
  useStorage,
  useToast,
  callOnce,
  useDialog,
  watch,
  useMaps,
  useMetrics,
  useUser,
  useCreators,
  useBookmarks,
  type UserTier,
} from "#imports";

import SpotView from "~~/app/blocks/SpotView/SpotView.vue";
import MPBookmarkSpot from "~/components/dialogs/MPBookmarkSpot.vue";
import MPAuth from "~/components/dialogs/MPAuth/MPAuth.vue";

const route = useRoute();
const { showError } = useToast();
const { formattedImageUrl } = useStorage();
const { openDialog } = useDialog();
const { trackEvent } = useMetrics();
const spotStore = useSpots();
const mapStore = useMaps();
const creatorStore = useCreators();
const userStore = useUser();
const bookmarkStore = useBookmarks();

const props = defineProps<{
  scrollingContainer: HTMLElement;
}>();

const emit = defineEmits<{
  close: [];
  pauseTimer: [];
  resumeTimer: [];
  resetTimer: [];
  next: [];
  prev: [];
  subscribe: [value: UserTier];
  preview: [value: boolean];
}>();

const SPOT_VIEW_LIMIT = 3;

const viewSpotCounter = (spotId: string) => {
  if (bookmarkStore.spotBookmarks.length > 0) return;

  bookmarkStore.viewedSpots++;

  if (bookmarkStore.viewedSpots % SPOT_VIEW_LIMIT === 0) {
    if (!userStore.user.id) {
      openDialog(MPAuth, { startOn: "signup" });
    } else {
      trackEvent("save-this-spot-dialog", {
        creator_id: data.value.creator.id,
        spot_id: data.value.id,
        map_id: data.value.map.id,
      });
      openDialog(
        MPBookmarkSpot,
        { spotId },
        { closeBtnColor: "var(--color-indigo-400)" },
      );
    }
  }
};

const data = computed(() => {
  const spot = spotStore.spotBySlugOrId!;
  const map = mapStore.getById(spot?.map_id).value!;
  const creator = creatorStore.getById(map?.user_id).value!;

  return {
    ...spot,
    map,
    creator,
  };
});

const loadData = async () => {
  if (
    typeof route.params.spotId !== "string" &&
    typeof route.params.spotSlug !== "string"
  ) {
    return;
  }
  try {
    // this page serves more than one "page" endpoint, so spotId and spotSlug are used
    const spot = await spotStore.fetchBySlugOrId(
      (route.params.spotId ?? route.params.spotSlug) as string,
    );
    const map = await mapStore.fetchById(spot.value!.map_id);
    await creatorStore.fetchById(map.value!.user_id);

    viewSpotCounter(spot.value!.id);
  } catch (e) {
    showError("Could not fetch spot", e);
  }
};

await callOnce(loadData, { mode: "navigation" });
watch(() => [route.params.spotId, route.params.spotSlug], loadData);

useHead(() => ({
  name: data.value.name,
  meta: [
    {
      name: "description",
      content: data.value.summary,
    },
  ],
}));

defineOgImageComponent(
  "Default",
  {
    name: data.value.name,
    description:
      data.value.summary?.split(" ").slice(0, 10).join(" ") +
      ((data.value.summary?.length ?? 0) > 90 ? "..." : ""),
  },
  {
    url: formattedImageUrl(data.value.cover_image_url!, 1200, 600, 75),
  },
);
</script>
<template>
  <SpotView
    v-if="data?.id"
    :spotId="data.id"
    :scrollingContainer="props.scrollingContainer"
    @prev="emit('prev')"
    @next="emit('next')"
    @close="emit('close')"
    @pauseTimer="emit('pauseTimer')"
    @resumeTimer="emit('resumeTimer')"
    @resetTimer="emit('resetTimer')"
    @subscribe="emit('subscribe', $event)"
    @preview="(value) => emit('preview', value)"
  />
</template>
